import { Text } from "@solomon-apps/shared/src/components/ui/text";
import React, { useId } from "react";

import { HintTip } from "../../shared/HintTip";

/**
 * Chart section chrome for the Reporting landing, flat on the page (no card):
 * an `h2` title (with an optional on-demand hint) and an optional header
 * action over a full-width divider, then the body. No description line: the
 * title names the chart and the axes carry the rest.
 */
export function ReportPanel({
    title,
    hint,
    action,
    children
}: {
    title: string;
    /** The definition behind the figure, shown on demand beside the title. */
    hint?: string;
    action?: React.ReactNode;
    children: React.ReactNode;
}): React.ReactElement {
    const headingId = useId();
    return (
        <section aria-labelledby={headingId}>
            <div className="flex items-center justify-between gap-4 flex-wrap pb-3 border-b border-line-base">
                <div className="flex items-center gap-2 min-w-0">
                    <Text
                        variant="h2"
                        render={<h2 id={headingId} />}
                        className="text-foreground-base"
                    >
                        {title}
                    </Text>
                    {hint ? (
                        <HintTip text={hint} label={`About ${title}`} />
                    ) : null}
                </div>
                {action}
            </div>
            {children}
        </section>
    );
}

/**
 * Stands in for the plot when the selected range holds too few points to
 * chart, while the panel's full history does not. Compact on purpose: a
 * tall empty box is the thing the visibility rule exists to remove.
 */
export function SparseRangeNote(): React.ReactElement {
    return (
        <div className="py-8 flex items-center justify-center">
            <Text
                variant="small"
                render={<p />}
                className="text-foreground-neutral text-center"
            >
                Not enough history in this range. Try a longer one.
            </Text>
        </div>
    );
}
